import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Request, RequestLinkType, RequestLink, StatusType } from 'src/app/request-v2/models/request.model';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_STATUSES } from 'src/app/request-v2/constants/request-status.constants';
import { MatDialog } from '@angular/material/dialog';
import { CreateRequestDialogComponent } from 'src/app/request-v2/components/create-request-dialog/create-request-dialog.component';

@Component({
    selector: 'app-request-links',
    templateUrl: './request-links.component.html',
    styleUrls: ['./request-links.component.scss'],
})
export class RequestLinksComponent implements OnInit, OnDestroy {
    @Input() request: Request;
    @Input() canEdit = false;
    @Output() requestChanged = new EventEmitter<Request>();
    @Output() navigateToRequest = new EventEmitter<Request>();

    isLinking = false;
    searchTerm = '';
    searchSubject = new Subject<string>();
    searchResults: Request[] = [];
    isSearching = false;

    linkTypes = [
        { value: RequestLinkType.BLOCKS, label: 'request_v2.links.types.BLOCKS' },
        { value: RequestLinkType.IS_BLOCKED_BY, label: 'request_v2.links.types.IS_BLOCKED_BY' },
        { value: RequestLinkType.IS_DUPPLICATE_OF, label: 'request_v2.links.types.IS_DUPPLICATE_OF' },
        { value: RequestLinkType.IS_LINKED_TO, label: 'request_v2.links.types.IS_LINKED_TO' },
    ];

    newLinkType: RequestLinkType = RequestLinkType.BLOCKS;

    linkToDelete: RequestLink | null = null;

    private searchSubscription: Subscription;

    // Open dropdown state for existing links
    openDropdownId: string | null = null;

    constructor(
        private requestService: RequestsV2ImplService,
        private eRef: ElementRef,
        private translateService: TranslateService,
        private dialog: MatDialog,
    ) {}

    @HostListener('document:click', ['$event'])
    clickout(event: any) {
        if (this.linkToDelete) {
            const inConfirm = event.target.closest('.confirm-yes') || event.target.closest('.confirm-no') || event.target.closest('.confirm-label');
            if (!inConfirm) {
                this.linkToDelete = null;
            }
        }

        if (!this.eRef.nativeElement.contains(event.target)) {
            this.openDropdownId = null;
            this.searchResults = [];
        } else {
            const clickedInsidePill = event.target.closest('.relation-pill') || event.target.closest('.relation-pill-md');
            if (!clickedInsidePill) {
                this.openDropdownId = null;
            }
            const clickedInsideSearch = event.target.closest('.link-search');
            if (!clickedInsideSearch) {
                this.searchResults = [];
            }
        }
    }

    ngOnInit(): void {
        this.searchSubscription = this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (!term || term.length < 2) {
                        return [];
                    }
                    this.isSearching = true;
                    // Pass search param. We reuse getRequests which accepts FilterOptions
                    return this.requestService.getRequests({ search: term } as any, 1, 10);
                }),
            )
            .subscribe(
                (results: any) => {
                    // Type as any first to avoid strict mismatch if PaginatedResult doesn't match perfectly with array return
                    this.isSearching = false;
                    if (results && results.docs) {
                        this.searchResults = results.docs.filter((r: Request) => r._id !== this.request._id);
                    } else {
                        this.searchResults = [];
                    }
                },
                (err) => {
                    this.isSearching = false;
                    console.error(err);
                },
            );
    }

    ngOnDestroy() {
        this.searchSubscription?.unsubscribe();
    }

    onSearch(term: string) {
        this.searchTerm = term;
        this.searchSubject.next(term);

        if (term.length < 2) {
            this.searchResults = [];
            this.isSearching = false;
        }
    }

    clearSearch() {
        this.searchTerm = '';
        this.searchResults = [];
        this.isSearching = false;
    }

    startLinking() {
        this.isLinking = true;
        this.newLinkType = RequestLinkType.BLOCKS; // Reset default
    }

    cancelLinking() {
        this.isLinking = false;
        this.searchTerm = '';
        this.searchResults = [];
        this.isSearching = false;
    }

    selectRequestToLink(targetRequest: Request) {
        if (!this.request || !this.request._id) return;

        this.requestService
            .linkRequest(this.request._id, {
                type: this.newLinkType,
                linkedRequestId: targetRequest._id,
            })
            .subscribe((updatedRequest) => {
                this.requestChanged.emit(updatedRequest);
                this.cancelLinking();
            });
    }

    createAndLinkRequest() {
        const linkType = this.newLinkType;
        this.cancelLinking();

        const dialogRef = this.dialog.open(CreateRequestDialogComponent, {
            width: '80vw',
            height: '90vh',
            panelClass: 'fullscreen-dialog',
            data: {
                preLinkedRequest: this.request,
                preLinkedType: linkType,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                this.refreshCurrentRequest();
                return;
            }

            const linkedRequestObjectId = result?.createdRequestObjectId;
            if (linkedRequestObjectId) {
                this.linkRequestById(linkedRequestObjectId, linkType);
                return;
            }

            const createdRequestId = result?.createdRequestId;
            if (!createdRequestId) {
                this.refreshCurrentRequest();
                return;
            }

            this.requestService.getRequests({ search: createdRequestId } as any, 1, 10).subscribe((searchResult: any) => {
                const createdRequest = (searchResult?.docs || []).find((request: Request) => request.requestId === createdRequestId);
                if (!createdRequest?._id) {
                    this.refreshCurrentRequest();
                    return;
                }
                this.linkRequestById(createdRequest._id, linkType);
            });
        });
    }

    private linkRequestById(linkedRequestId: string, linkType: RequestLinkType) {
        if (!linkedRequestId || !this.request?._id) return;

        this.requestService
            .linkRequest(this.request._id, {
                type: linkType,
                linkedRequestId,
            })
            .subscribe({
                next: (updatedRequest) => {
                    this.requestChanged.emit(updatedRequest);
                },
                error: () => {
                    this.refreshCurrentRequest();
                },
            });
    }

    refreshCurrentRequest() {
        if (!this.request?._id) return;

        this.requestService.getRequestDetails(this.request._id).subscribe((updatedRequest) => {
            this.requestChanged.emit(updatedRequest);
        });
    }

    toggleDropdown(linkId: string | undefined) {
        if (!linkId) return;
        if (this.openDropdownId === linkId) {
            this.openDropdownId = null;
        } else {
            this.openDropdownId = linkId;
        }
    }

    onLinkTypeChange(link: RequestLink, newType: RequestLinkType) {
        if (link.type === newType) return;
        if (!link.linkedRequest?._id) return;

        this.openDropdownId = null;

        this.requestService
            .updateLinkRequest(this.request._id, {
                type: newType,
                linkedRequestId: link.linkedRequest._id,
            })
            .subscribe((updatedRequest) => {
                this.requestChanged.emit(updatedRequest);
            });
    }

    confirmDelete(link: RequestLink) {
        this.linkToDelete = link;
    }

    cancelDelete() {
        this.linkToDelete = null;
    }

    deleteLink() {
        if (!this.linkToDelete?.linkedRequest?._id) return;
        this.requestService.deleteLinkRequest(this.request._id, this.linkToDelete.linkedRequest._id).subscribe((updatedRequest) => {
            this.requestChanged.emit(updatedRequest);
            this.linkToDelete = null;
        });
    }

    getStatus(req: any): StatusType | undefined {
        if (!req || !req.category || !req.category.statuses) return undefined;
        return req.category.statuses.find((s: any) => s._id === req.state || s.id === req.state);
    }

    getStatusLabel(status: StatusType | undefined): string {
        if (!status) return '';
        if (status.isDefault) {
            const defaultStatus = DEFAULT_STATUSES.find((s) => s.nameEn === status.name || s.nameFr === status.name);
            if (defaultStatus) {
                return this.translateService.currentLang === 'fr' ? defaultStatus.nameFr : defaultStatus.nameEn;
            }
        }
        return status.name;
    }

    private translateStatus(s: StatusType): StatusType {
        const nameLower = s.name?.toLowerCase();
        const defaultStatus = DEFAULT_STATUSES.find((d) => d.nameEn.toLowerCase() === nameLower || d.nameFr.toLowerCase() === nameLower);
        if (defaultStatus) {
            return { ...s, name: this.translateService.currentLang === 'fr' ? defaultStatus.nameFr : defaultStatus.nameEn };
        }
        return s;
    }

    getTranslatedStatuses(req: any): StatusType[] {
        if (!req?.category?.statuses) return [];
        return req.category.statuses.map((s: StatusType) => this.translateStatus(s));
    }

    getTranslatedStatus(req: any): StatusType | undefined {
        const status = this.getStatus(req);
        return status ? this.translateStatus(status) : undefined;
    }

    getLabelForType(type: RequestLinkType): string {
        const found = this.linkTypes.find((t) => t.value === type);
        return found ? found.label : type;
    }

    getLinkTitle(req: any): string {
        return req?.subCategory?.name || req?.category?.name || '';
    }

    openLinkedRequest(linkedRequest: Request): void {
        this.navigateToRequest.emit(linkedRequest);
    }
}
